import React, { type JSX } from "react";
import { createPortal } from "react-dom";
import toastObj, { type ToastPosition, type DefaultToastOptions, type Toast, type ToastType, resolveValue } from "react-hot-toast/headless";

import { SupToast } from "../../../components/UI/Notification/Toast";
import { usePortalRoot } from "../../../hooks/usePortalRoot";
import { type CommonColors } from "../../../types/colors";

import { Toaster } from "./components/Toaster";

export interface ToasterProps {
  portal?: boolean;
  position?: ToastPosition;
  toastOptions?: DefaultToastOptions;
  reverseOrder?: boolean;
  gutter?: number;
  containerStyle?: React.CSSProperties;
  containerClassName?: string;
  children?: (toast: Toast) => JSX.Element;
  limit?: number;
}

const getToastColor = (type: ToastType): CommonColors => {
  switch (type) {
    case "error":
    case "success":
      return type;
    default:
      return "primary";
  }
};

const renderDefaultToast = (toast: Toast): JSX.Element => {
  const message = resolveValue(toast.message, toast);

  const handleClose = () => {
    toastObj.remove(toast.id);
  };

  if (toast.type === "custom") {
    return message as JSX.Element;
  }

  return typeof message === "string" ? (
    <SupToast color={getToastColor(toast.type)} icon={toast.icon} title={message} onClose={handleClose} />
  ) : (
    <SupToast color={getToastColor(toast.type)} icon={toast.icon} content={message} onClose={handleClose} />
  );
};

export const SupToaster = ({ portal = false, children = renderDefaultToast, ...restProps }: ToasterProps) => {
  const portalRoot = usePortalRoot(portal);
  const component = <Toaster {...restProps}>{children}</Toaster>;

  return portalRoot ? createPortal(component, portalRoot) : component;
};

export { default as toast, resolveValue, type Toast, type ToastType, type ToastOptions } from "react-hot-toast/headless";
